import React, { useCallback, type ReactElement } from 'react';

import { Country } from '@pushwoosh/geo-ip';
import { ReCAPTCHA, useRecaptcha } from '~/src/components/recaptcha';
import {
  EditSVG,
  EmailSVG,
  HomeSVG,
  IndustrySVG,
  PhoneSVG,
  StatsSVG,
  UserSVG,
} from '~/src/assets/icons';
import { Button } from '~/src/ui/button';
import { Checkbox } from '~/src/ui/checkbox';
import { ErrorMessage } from '~/src/ui/error-message';
import { Select } from '~/src/ui/select';
import { TextArea } from '~/src/ui/text-area';
import { TextInput } from '~/src/ui/text-input';
import { sendToGoogleTagManager } from '../../helpers/sendToGoogleTagManager';

import { FormProps, FormName } from './form.types';
import { FormBox, FormField, FormGroup } from './form.styles';

const mau = [
  { label: 'Pre-launch', value: 'Pre-launch' },
  { label: 'Under 10k', value: 'Under 10k' },
  { label: '10k - 50k', value: '10k - 50k' },
  { label: '50k - 100k', value: '50k - 100k' },
  { label: '100k - 500k', value: '100k - 500k' },
  { label: '500k - 1M', value: '500k - 1M' },
  { label: '1M+', value: '1M+' },
];

const countries: Array<{ value: string, label: string }> = Object.entries(Country).map(([key, country]) => ({ value: key, label: country }));
// added country without iso
countries.push({ label: 'West Bank and Gaza', value: 'West Bank and Gaza' });

export function Form(props: FormProps): ReactElement {
  const { getRecaptchaToken } = useRecaptcha();
  const {
    form,
    source,
    formFields,
    hidePhone,
  } = props;

  const { Subscribe } = formFields;

  const onSubmitClick = useCallback(
    async (event) => {
      event.preventDefault();
      const token = await getRecaptchaToken();

      form.setFieldValue(FormName.RECAPTCHA_RESPONSE, token);
      form.submitForm();

      if (form.values[FormName.ACCEPT_EDUCATION_CONTENT]) {
        sendToGoogleTagManager('event', 'newsletter sing ups');
      }
    },
    [form, getRecaptchaToken],
  );

  const onPhoneChange = useCallback(
    ({ name, value }) => {
      const numericValue = value.replace(/\D/g, '');
      const plusValue = numericValue && numericValue.replace(/^/, '+');

      form.setFieldValue(name, plusValue.trim());
    },
    [form],
  );

  return (
    <FormBox id={`form-id-${source}`}>
      {/* FIRST NAME */}
      <FormGroup>
        <div>
          <FormField>
            <TextInput
              name={FormName.FIRST_NAME}
              value={form.values[FormName.FIRST_NAME]}
              placeholder={formFields.firstName}
              icon={(
                <UserSVG />
              )}
              isErrored={form.touched[FormName.FIRST_NAME] && !!form.errors[FormName.FIRST_NAME]}
              isDisabled={form.isSubmitting}
              onChange={({ name, value }): void => form.setFieldValue(name, value)}
              onBlur={({ name }): void => form.setFieldTouched(name)}
            />
            {form.touched[FormName.FIRST_NAME] && (
              <ErrorMessage message={form.errors[FormName.FIRST_NAME]} />
            )}
          </FormField>
        </div>
        <div>
          {/* LAST NAME */}
          <FormField>
            <TextInput
              name={FormName.LAST_NAME}
              value={form.values[FormName.LAST_NAME]}
              placeholder={formFields.lastName}
              icon={(
                <UserSVG />
              )}
              isErrored={form.touched[FormName.LAST_NAME] && !!form.errors[FormName.LAST_NAME]}
              isDisabled={form.isSubmitting}
              onChange={({ name, value }): void => form.setFieldValue(name, value)}
              onBlur={({ name }): void => form.setFieldTouched(name)}
            />
            {form.touched[FormName.LAST_NAME] && (
              <ErrorMessage message={form.errors[FormName.LAST_NAME]} />
            )}
          </FormField>
        </div>
      </FormGroup>

      {/* EMAIL */}
      <FormField>
        <TextInput
          name={FormName.EMAIL}
          value={form.values[FormName.EMAIL]}
          placeholder={formFields.email}
          icon={(
            <EmailSVG />
          )}
          isErrored={form.touched[FormName.EMAIL] && !!form.errors[FormName.EMAIL]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.EMAIL] && (
          <ErrorMessage message={form.errors[FormName.EMAIL]} />
        )}
      </FormField>

      {/* PHONE */}
      {!hidePhone && (
        <FormField>
          <TextInput
            name={FormName.PHONE}
            value={form.values[FormName.PHONE]}
            placeholder={formFields.phone}
            icon={(<PhoneSVG />)}
            isErrored={form.touched[FormName.PHONE] && !!form.errors[FormName.PHONE]}
            isDisabled={form.isSubmitting}
            onChange={onPhoneChange}
            onBlur={({ name }): void => form.setFieldTouched(name)}
          />
          {form.touched[FormName.PHONE] && (
            <ErrorMessage message={form.errors[FormName.PHONE]} />
          )}
        </FormField>
      )}

      {/* COMPANY */}
      <FormField>
        <TextInput
          name={FormName.COMPANY}
          value={form.values[FormName.COMPANY]}
          placeholder={formFields.company}
          icon={(
            <IndustrySVG />
          )}
          isErrored={form.touched[FormName.COMPANY] && !!form.errors[FormName.COMPANY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.COMPANY] && (
          <ErrorMessage message={form.errors[FormName.COMPANY]} />
        )}
      </FormField>

      {/* MAU */}
      <FormField>
        <Select
          name={FormName.MAU}
          value={form.values[FormName.MAU]}
          placeholder={formFields.projects}
          icon={(
            <StatsSVG />
          )}
          options={mau}
          isErrored={form.touched[FormName.MAU] && !!form.errors[FormName.MAU]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.MAU] && (
          <ErrorMessage message={form.errors[FormName.MAU]} />
        )}
      </FormField>

      {/* COUNTRY */}
      <FormField>
        <Select
          name={FormName.COUNTRY}
          value={form.values[FormName.COUNTRY]}
          placeholder={formFields.location}
          icon={(
            <HomeSVG />
          )}
          options={countries}
          isErrored={form.touched[FormName.COUNTRY] && !!form.errors[FormName.COUNTRY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.COUNTRY] && (
          <ErrorMessage message={form.errors[FormName.COUNTRY]} />
        )}
      </FormField>

      {/* MESSAGE */}
      <FormField>
        <TextArea
          name={FormName.MESSAGE}
          value={form.values[FormName.MESSAGE]}
          placeholder={formFields.message}
          icon={(
            <EditSVG />
          )}
          isErrored={form.touched[FormName.MESSAGE] && !!form.errors[FormName.MESSAGE]}
          isDisabled={form.isSubmitting}
          onChange={({ name, value }): void => form.setFieldValue(name, value)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        />
        {form.touched[FormName.MESSAGE] && (
          <ErrorMessage message={form.errors[FormName.MESSAGE]} />
        )}
      </FormField>

      {/* PRIVACY POLICY */}
      <FormField $isBigGap>
        <Checkbox
          name={FormName.ACCEPT_PRIVACY_POLICY}
          value={form.values[FormName.ACCEPT_PRIVACY_POLICY]}
          isChecked={form.values[FormName.ACCEPT_PRIVACY_POLICY]}
          isErrored={form.touched[FormName.ACCEPT_PRIVACY_POLICY] && !!form.errors[FormName.ACCEPT_PRIVACY_POLICY]}
          isDisabled={form.isSubmitting}
          onChange={({ name, isChecked }): void => form.setFieldValue(name, isChecked)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        >
          <Subscribe />
        </Checkbox>
        {form.touched[FormName.ACCEPT_PRIVACY_POLICY] && (
          <ErrorMessage message={form.errors[FormName.ACCEPT_PRIVACY_POLICY]} />
        )}
      </FormField>

      {/* EDUCATION CONTENT */}
      <FormField $isSmallGap>
        <Checkbox
          name={FormName.ACCEPT_EDUCATION_CONTENT}
          value={form.values[FormName.ACCEPT_EDUCATION_CONTENT]}
          isChecked={form.values[FormName.ACCEPT_EDUCATION_CONTENT]}
          isErrored={form.touched[FormName.ACCEPT_EDUCATION_CONTENT] && !!form.errors[FormName.ACCEPT_EDUCATION_CONTENT]}
          isDisabled={form.isSubmitting}
          onChange={({ name, isChecked }): void => form.setFieldValue(name, isChecked)}
          onBlur={({ name }): void => form.setFieldTouched(name)}
        >
          {formFields.agreement}
        </Checkbox>
        {form.touched[FormName.ACCEPT_EDUCATION_CONTENT] && (
          <ErrorMessage message={form.errors[FormName.ACCEPT_EDUCATION_CONTENT]} />
        )}
      </FormField>
      <ReCAPTCHA />
      <Button
        type="submit"
        view="primary"
        id="demoformFields"
        isDisabled={form.isSubmitting}
        onClick={onSubmitClick}
      >
        {formFields.submit}
      </Button>
    </FormBox>
  );
}
